import Phaser from 'phaser';
import { GameConfig } from '../config/GameConfig';
import { ProductType, Products } from '../config/ProductData';
import { orderManager } from '../systems/OrderManager';
import { productionState } from '../systems/ProductionState';

type MachineState = 'idle' | 'working' | 'ready';

interface MachineData {
  sprite: Phaser.GameObjects.Sprite;
  state: MachineState;
  timer: number;
  duration: number;
  progressBar: Phaser.GameObjects.Graphics;
  readyGlow: Phaser.GameObjects.Graphics;
  label: Phaser.GameObjects.Text;
  upgradeBtn: Phaser.GameObjects.Container | null;
  upgradeLabel: Phaser.GameObjects.Text | null;
}

interface Particle {
  x: number; y: number; vx: number; vy: number; life: number; maxLife: number; color: number; size: number;
}

export class GameScene extends Phaser.Scene {
  private machineA!: MachineData;
  private machineB!: MachineData;
  private sessionTimeLeft: number = GameConfig.sessionDuration;
  private gameActive: boolean = true;
  private particles: Particle[] = [];
  private particleGraphics!: Phaser.GameObjects.Graphics;

  // HUD
  private creditsText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private wipText!: Phaser.GameObjects.Text;
  private finishedText!: Phaser.GameObjects.Text;
  private orderBoardGraphics!: Phaser.GameObjects.Graphics;
  private orderTexts: Phaser.GameObjects.Text[] = [];
  private warningText!: Phaser.GameObjects.Text;
  private productButtons: Phaser.GameObjects.Container[] = [];
  private deliveryButton!: Phaser.GameObjects.Container;
  private ordersCompletedText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'GameScene' });
  }

  create(): void {
    this.gameActive = true;
    this.sessionTimeLeft = GameConfig.sessionDuration;
    this.particles = [];
    orderManager.reset();
    productionState.reset();
    orderManager.start();

    // Background
    const bg = this.add.image(480, 320, 'factory_bg');
    bg.setDisplaySize(960, 640);

    // Title
    this.add.text(480, 20, 'TINY TOYS FACTORY', {
      fontSize: '28px', color: '#ffffff', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 4,
    }).setOrigin(0.5);

    // Particle graphics layer
    this.particleGraphics = this.add.graphics();

    // Machine A
    this.machineA = this.createMachine(220, 260, 'assembly', 'Máy Lắp ráp', true);
    // Machine B
    this.machineB = this.createMachine(640, 260, 'paint', 'Máy Sơn & Đóng gói', false);

    // Product selector
    this.createProductSelector();

    // Delivery button
    this.createDeliveryButton();

    // HUD
    this.createHUD();

    // Order Board (drawn with Graphics, no spr_panel)
    this.createOrderBoard();

    // Warning
    this.warningText = this.add.text(480, 180, '', {
      fontSize: '20px', color: '#ff4444', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5).setVisible(false).setDepth(100);
  }

  update(time: number, delta: number): void {
    if (!this.gameActive) return;
    const dt = delta;

    // Session timer
    this.sessionTimeLeft -= dt / 1000;
    if (this.sessionTimeLeft <= 0) {
      this.sessionTimeLeft = 0;
      this.endGame();
      return;
    }

    // Order
    orderManager.update(dt);
    if (orderManager.CompletedCount >= GameConfig.ordersToWin) {
      this.endGame(true);
      return;
    }

    // Machines
    this.updateMachine(this.machineA, dt, true);
    this.updateMachine(this.machineB, dt, false);

    // Particles
    this.updateParticles(dt);

    // HUD
    this.updateHUD();
    this.updateOrderBoard();
    this.updateUpgradeButtons();
  }

  // ==================== MACHINE ====================
  private createMachine(x: number, y: number, key: string, name: string, isA: boolean): MachineData {
    const sprite = this.add.sprite(x, y, key).setScale(0.55).setInteractive({ useHandCursor: true });
    const label = this.add.text(x, y + 85, name, {
      fontSize: '15px', color: '#ffffff', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5);

    const progressBar = this.add.graphics();
    const readyGlow = this.add.graphics();

    // Upgrade button (small, below machine)
    const upgradeContainer = this.add.container(x, y + 115);
    const upBg = this.add.rectangle(0, 0, 110, 22, 0x228822, 0.9)
      .setStrokeStyle(1, 0x44ff44)
      .setInteractive({ useHandCursor: true });
    const upTxt = this.add.text(0, 0, 'UPGRADE', {
      fontSize: '11px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5);
    upgradeContainer.add([upBg, upTxt]);
    upgradeContainer.setVisible(false);

    upBg.on('pointerdown', () => {
      this.pressButtonEffect(upgradeContainer);
      const cost = isA
        ? GameConfig.machineAUpgradeCosts[productionState.MachineALevel]
        : GameConfig.machineBUpgradeCosts[productionState.MachineBLevel];
      if (orderManager.spendCredits(cost)) {
        if (isA) productionState.upgradeMachineA(); else productionState.upgradeMachineB();
        this.showFloatingText(x, y - 40, 'Đã nâng cấp!', '#44ff44');
        this.updateHUD();
      }
    });

    const machine: MachineData = {
      sprite, state: 'idle', timer: 0,
      duration: isA ? productionState.getMachineADuration() : productionState.getMachineBDuration(),
      progressBar, readyGlow, label,
      upgradeBtn: upgradeContainer, upgradeLabel: upTxt,
    };

    sprite.on('pointerdown', () => this.onMachineClick(machine, isA));
    return machine;
  }

  private onMachineClick(machine: MachineData, isA: boolean): void {
    if (!this.gameActive) return;
    this.pressButtonEffect(machine.sprite);

    if (isA) {
      if (machine.state === 'idle') {
        machine.state = 'working';
        machine.timer = productionState.getMachineADuration();
        machine.duration = machine.timer;
      } else if (machine.state === 'ready') {
        machine.state = 'idle';
        productionState.addWIP();
        this.emitParticles(machine.sprite.x, machine.sprite.y, 0x44ff44, GameConfig.particleCount);
        this.showFloatingText(machine.sprite.x, machine.sprite.y - 50, '+1 WIP', '#44ff44');
      }
    } else {
      if (machine.state === 'idle') {
        if (productionState.consumeWIP()) {
          machine.state = 'working';
          machine.timer = productionState.getMachineBDuration();
          machine.duration = machine.timer;
        } else {
          this.showWarning('Không đủ WIP!');
        }
      } else if (machine.state === 'ready') {
        machine.state = 'idle';
        const product = productionState.SelectedProduct;
        productionState.addFinished(product);
        const info = Products[product];
        this.emitParticles(machine.sprite.x, machine.sprite.y, info.color, GameConfig.particleCount);
        this.showFloatingText(machine.sprite.x, machine.sprite.y - 50, `+1 ${info.label}`, '#44ff44');
      }
    }
  }

  private updateMachine(machine: MachineData, delta: number, isA: boolean): void {
    machine.progressBar.clear();
    machine.readyGlow.clear();

    if (machine.state === 'working') {
      machine.timer -= delta;
      const progress = 1 - (machine.timer / machine.duration);
      const barW = 120, barH = 12;
      const bx = machine.sprite.x - barW / 2;
      const by = machine.sprite.y - 75;

      machine.progressBar.fillStyle(0x222222, 0.8);
      machine.progressBar.fillRect(bx, by, barW, barH);
      machine.progressBar.fillStyle(0x00ff88, 1);
      machine.progressBar.fillRect(bx, by, barW * Phaser.Math.Clamp(progress, 0, 1), barH);
      machine.progressBar.lineStyle(2, 0xffffff, 0.8);
      machine.progressBar.strokeRect(bx, by, barW, barH);

      if (machine.timer <= 0) {
        machine.state = 'ready';
        machine.timer = 0;
      }
    } else if (machine.state === 'ready') {
      const alpha = 0.25 + Math.sin(this.time.now / 150) * 0.25;
      machine.readyGlow.fillStyle(0xffff00, alpha);
      machine.readyGlow.fillCircle(machine.sprite.x, machine.sprite.y, 65);

      machine.progressBar.fillStyle(0x00ff88, 0.9);
      const barW = 120, barH = 12;
      machine.progressBar.fillRect(machine.sprite.x - barW / 2, machine.sprite.y - 75, barW, barH);
      machine.progressBar.lineStyle(2, 0xffffff, 0.9);
      machine.progressBar.strokeRect(machine.sprite.x - barW / 2, machine.sprite.y - 75, barW, barH);
    }
  }

  private updateUpgradeButtons(): void {
    // Machine A
    const aLevel = productionState.MachineALevel;
    if (aLevel < 2 && this.machineA.upgradeBtn && this.machineA.upgradeLabel) {
      const cost = GameConfig.machineAUpgradeCosts[aLevel];
      const can = orderManager.Credits >= cost;
      this.machineA.upgradeBtn.setVisible(true);
      this.machineA.upgradeLabel.setText(`UPGRADE $${cost}`);
      const bg = this.machineA.upgradeBtn.getAt(0) as Phaser.GameObjects.Rectangle;
      bg.setFillStyle(can ? 0x228822 : 0x444444);
    } else if (this.machineA.upgradeBtn) {
      this.machineA.upgradeBtn.setVisible(false);
    }

    // Machine B
    const bLevel = productionState.MachineBLevel;
    if (bLevel < 2 && this.machineB.upgradeBtn && this.machineB.upgradeLabel) {
      const cost = GameConfig.machineBUpgradeCosts[bLevel];
      const can = orderManager.Credits >= cost;
      this.machineB.upgradeBtn.setVisible(true);
      this.machineB.upgradeLabel.setText(`UPGRADE $${cost}`);
      const bg = this.machineB.upgradeBtn.getAt(0) as Phaser.GameObjects.Rectangle;
      bg.setFillStyle(can ? 0x228822 : 0x444444);
    } else if (this.machineB.upgradeBtn) {
      this.machineB.upgradeBtn.setVisible(false);
    }
  }

  // ==================== PRODUCT SELECTOR ====================
  private createProductSelector(): void {
    const container = this.add.container(420, 520);
    const label = this.add.text(0, -30, 'Chọn loại sản phẩm:', {
      fontSize: '14px', color: '#ffffff', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 2,
    }).setOrigin(0.5);
    container.add(label);

    const types = [ProductType.ToyCar, ProductType.Robot, ProductType.LuxuryDoll];
    const offsets = [-90, 0, 90];

    types.forEach((type, i) => {
      const info = Products[type];
      const btn = this.add.container(offsets[i], 0);
      const bg = this.add.rectangle(0, 0, 70, 36, info.color, 0.9)
        .setStrokeStyle(2, 0xffffff)
        .setInteractive({ useHandCursor: true });
      const txt = this.add.text(0, 0, info.label, {
        fontSize: '11px', color: '#ffffff', fontStyle: 'bold',
      }).setOrigin(0.5);
      btn.add([bg, txt]);
      container.add(btn);

      bg.on('pointerdown', () => {
        this.pressButtonEffect(btn);
        productionState.setSelectedProduct(type);
        this.updateProductSelector();
      });
      this.productButtons.push(btn);
    });
    this.updateProductSelector();
  }

  private updateProductSelector(): void {
    const selected = productionState.SelectedProduct;
    this.productButtons.forEach((btn, i) => {
      const types = [ProductType.ToyCar, ProductType.Robot, ProductType.LuxuryDoll];
      const isSel = types[i] === selected;
      const bg = btn.getAt(0) as Phaser.GameObjects.Rectangle;
      bg.setStrokeStyle(isSel ? 4 : 2, isSel ? 0xffff00 : 0xffffff);
    });
  }

  // ==================== DELIVERY ====================
  private createDeliveryButton(): void {
    this.deliveryButton = this.add.container(850, 540);
    const bg = this.add.rectangle(0, 0, 150, 48, 0xffaa00, 0.95)
      .setStrokeStyle(3, 0xffffff)
      .setInteractive({ useHandCursor: true });
    const txt = this.add.text(0, 0, 'GIAO HÀNG', {
      fontSize: '16px', color: '#000000', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.deliveryButton.add([bg, txt]);

    bg.on('pointerdown', () => {
      this.pressButtonEffect(this.deliveryButton);
      this.onDelivery();
    });
  }

  private onDelivery(): void {
    if (!this.gameActive) return;
    const order = orderManager.Order;
    if (!order || order.completed || order.expired) {
      this.showWarning('Không có đơn hàng nào!'); return;
    }
    // Find any product we have that matches an unfulfilled requirement
    let delivered = false;
    for (const req of order.requirements) {
      if (req.fulfilled < req.quantity && productionState.hasFinished(req.product)) {
        const result = orderManager.tryDeliver(req.product);
        if (result.success) {
          productionState.consumeFinished(req.product);
          delivered = true;
          this.showFloatingText(850, 500, `+$${result.reward}`, '#ffff00');
          if (result.completed) {
            this.cameras.main.shake(GameConfig.shakeDuration, GameConfig.shakeIntensity);
            this.emitParticles(850, 500, 0xffff00, 12);
            this.showFloatingText(480, 280, 'ĐƠN HOÀN THÀNH!', '#00ff00');
          }
          break;
        }
      }
    }
    if (!delivered) {
      this.showWarning('Không có thành phẩm phù hợp!');
    }
    this.updateHUD();
  }

  // ==================== HUD ====================
  private createHUD(): void {
    this.creditsText = this.add.text(20, 55, '$0', {
      fontSize: '26px', color: '#ffd700', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 4,
    });

    this.timerText = this.add.text(480, 55, '03:00', {
      fontSize: '26px', color: '#ffffff', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 4,
    }).setOrigin(0.5);

    this.wipText = this.add.text(20, 430, 'WIP: 0', {
      fontSize: '18px', color: '#44ff44', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    });

    this.finishedText = this.add.text(20, 460, 'Thành phẩm: 0', {
      fontSize: '18px', color: '#44aaff', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    });

    this.ordersCompletedText = this.add.text(20, 400, 'Đơn hoàn thành: 0/5', {
      fontSize: '18px', color: '#ffaa44', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    });
  }

  private updateHUD(): void {
    this.creditsText.setText(`$${orderManager.Credits}`);

    const mins = Math.floor(this.sessionTimeLeft / 60);
    const secs = Math.floor(this.sessionTimeLeft % 60);
    this.timerText.setText(`${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`);
    this.timerText.setColor(this.sessionTimeLeft < 30 ? '#ff4444' : '#ffffff');

    this.wipText.setText(`WIP: ${productionState.WIPCount}`);
    this.finishedText.setText(`Thành phẩm: ${productionState.totalFinished()}`);
    this.ordersCompletedText.setText(`Đơn hoàn thành: ${orderManager.CompletedCount}/${GameConfig.ordersToWin}`);
  }

  // ==================== ORDER BOARD (Graphics-based, no spr_panel) ====================
  private createOrderBoard(): void {
    this.orderBoardGraphics = this.add.graphics();
    const texts: Phaser.GameObjects.Text[] = [];
    for (let i = 0; i < 6; i++) {
      texts.push(this.add.text(0, 0, '', { fontSize: '13px', color: '#ffffff' }).setOrigin(0.5));
    }
    this.orderTexts = texts;
  }

  private updateOrderBoard(): void {
    const order = orderManager.Order;
    const g = this.orderBoardGraphics;
    g.clear();

    // Hide all texts first
    this.orderTexts.forEach(t => { t.setVisible(false); });

    if (!order || order.completed || order.expired) {
      // Draw empty panel
      this.drawOrderPanel(800, 140, 220, 140, 'Không có đơn');
      return;
    }

    const reqCount = order.requirements.length;
    const panelH = 130 + reqCount * 20;
    this.drawOrderPanel(800, 140, 220, panelH, 'ĐƠN HÀNG');

    let yOff = -panelH / 2 + 35;
    this.orderTexts[0].setPosition(800, 140 + yOff).setText(`Đối tác: ${order.partner}`).setVisible(true);
    yOff += 22;

    order.requirements.forEach((req, i) => {
      const info = Products[req.product];
      const colorStr = '#' + info.color.toString(16).padStart(6, '0');
      this.orderTexts[i + 1].setPosition(800, 140 + yOff)
        .setText(`${info.label}: ${req.fulfilled}/${req.quantity}`)
        .setColor(colorStr)
        .setVisible(true);
      yOff += 20;
    });

    yOff += 5;
    const timeRatio = order.timeRemaining / order.deadlineSeconds;
    const timeColor = timeRatio < 0.2 ? '#ff4444' : '#ffffff';
    this.orderTexts[4].setPosition(800, 140 + yOff)
      .setText(`Còn: ${Math.ceil(order.timeRemaining)}s`)
      .setColor(timeColor)
      .setVisible(true);
    yOff += 20;

    this.orderTexts[5].setPosition(800, 140 + yOff)
      .setText(`Thưởng: $${order.baseReward}`)
      .setColor('#ffff00')
      .setVisible(true);
  }

  private drawOrderPanel(x: number, y: number, w: number, h: number, title: string): void {
    const g = this.orderBoardGraphics;
    // Background
    g.fillStyle(0x0d1b2a, 0.92);
    g.fillRoundedRect(x - w / 2, y - h / 2, w, h, 12);
    // Border
    g.lineStyle(3, 0x00b4d8, 0.9);
    g.strokeRoundedRect(x - w / 2, y - h / 2, w, h, 12);
    // Title bar
    g.fillStyle(0x0077b6, 0.8);
    g.fillRoundedRect(x - w / 2 + 4, y - h / 2 + 4, w - 8, 28, 8);
    // Title text (reuse first text object)
    this.orderTexts[0].setPosition(x, y - h / 2 + 18)
      .setText(title)
      .setFontSize('15px')
      .setColor('#ffffff')
      .setFontStyle('bold')
      .setVisible(true);
  }

  // ==================== EFFECTS ====================
  private emitParticles(x: number, y: number, color: number, count: number): void {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 1 + Math.random() * 3;
      this.particles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 1,
        life: GameConfig.particleLife,
        maxLife: GameConfig.particleLife,
        color,
        size: 3 + Math.random() * 4,
      });
    }
  }

  private updateParticles(delta: number): void {
    this.particleGraphics.clear();
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= delta;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.05; // gravity
      const alpha = p.life / p.maxLife;
      this.particleGraphics.fillStyle(p.color, alpha);
      this.particleGraphics.fillCircle(p.x, p.y, p.size * alpha);
    }
  }

  private pressButtonEffect(target: Phaser.GameObjects.GameObject): void {
    this.tweens.add({
      targets: target,
      scaleX: 0.92,
      scaleY: 0.92,
      duration: 50,
      yoyo: true,
      ease: 'Quad.easeInOut',
    });
  }

  private showFloatingText(x: number, y: number, text: string, color: string): void {
    const txt = this.add.text(x, y, text, {
      fontSize: '20px', color, fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(50);
    this.tweens.add({
      targets: txt, y: y - 50, alpha: 0,
      duration: 900, ease: 'Power1',
      onComplete: () => txt.destroy(),
    });
  }

  private showWarning(msg: string): void {
    this.warningText.setText(msg).setVisible(true).setAlpha(1);
    this.tweens.add({
      targets: this.warningText, alpha: 0,
      duration: 1400, ease: 'Power1',
      onComplete: () => this.warningText.setVisible(false),
    });
  }

  private endGame(win: boolean = false): void {
    this.gameActive = false;
    this.scene.start('ResultScene', {
      win, credits: orderManager.Credits,
      completed: orderManager.CompletedCount,
      reputation: orderManager.Reputation,
    });
  }
}
